import React, { useMemo, useRef, useState } from "react";
import { TrendingUp } from "lucide-react";

/**
 * HiringTrendChart — new hires per month (last 6 months), visibility-scoped.
 *
 * Single-series line per the dataviz mark specs: 2px round-joined line, an
 * 8px end-marker with a 2px surface ring, a ~10% opacity area wash, hairline
 * solid gridlines, and a crosshair tooltip. One series → no legend box; the
 * card title names it.
 */
const BRAND = { light: "#2a78d6", dark: "#4a9be0" };

const HiringTrendChart = ({ data = [], theme = "light", language = "en" }) => {
  const dark = theme === "dark";
  const isRTL = language === "ar";
  const [hover, setHover] = useState(null); // index
  const wrapRef = useRef(null);

  const W = 560;
  const H = 200;
  const PAD = { top: 18, right: 26, bottom: 28, left: 34 };

  const series = useMemo(
    () =>
      (data || []).map((d) => ({
        label: d.month,
        value: Number(d.hires || 0),
      })),
    [data],
  );

  const maxVal = Math.max(1, ...series.map((d) => d.value));
  // clean tick ceiling (1/2/5 ladder)
  const niceMax = useMemo(() => {
    const candidates = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
    return candidates.find((c) => c >= maxVal) || maxVal;
  }, [maxVal]);

  const x = (i) =>
    PAD.left + (series.length <= 1 ? 0 : (i * (W - PAD.left - PAD.right)) / (series.length - 1));
  const y = (v) => H - PAD.bottom - (v / niceMax) * (H - PAD.top - PAD.bottom);

  const linePath = series.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(d.value)}`).join(" ");
  const areaPath =
    series.length > 1
      ? `${linePath} L ${x(series.length - 1)} ${H - PAD.bottom} L ${x(0)} ${H - PAD.bottom} Z`
      : "";

  const monthLabel = (ym) => {
    const [yy, mm] = String(ym).split("-").map(Number);
    if (!yy || !mm) return ym;
    const date = new Date(yy, mm - 1, 1);
    return date.toLocaleDateString(isRTL ? "ar" : "en", { month: "short" });
  };

  const color = dark ? BRAND.dark : BRAND.light;
  const surface = dark ? "#1e293b" : "#ffffff";
  const grid = dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)";
  const textSecondary = dark ? "rgba(255,255,255,0.55)" : "#64748b";
  const textPrimary = dark ? "#f1f5f9" : "#1e293b";

  const ticks = [0, niceMax / 2, niceMax].map((v) => Math.round(v));
  const last = series[series.length - 1];

  const handleMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || !series.length) return;
    const px = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    let bestDist = Infinity;
    series.forEach((_, i) => {
      const d = Math.abs(x(i) - px);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setHover(best);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative", minWidth: 0 }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: "100%", height: "auto", display: "block" }}
        role="img"
        aria-label={
          isRTL
            ? `تعيينات جديدة خلال آخر ٦ أشهر. آخر شهر: ${last ? last.value : 0}`
            : `New hires over the last 6 months. Latest month: ${last ? last.value : 0}`
        }
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        {/* hairline gridlines + y ticks */}
        {ticks.map((v) => (
          <g key={v}>
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke={grid} strokeWidth="1" />
            <text x={PAD.left - 8} y={y(v)} textAnchor="end" dominantBaseline="central" fontSize="10" fill={textSecondary}>
              {v}
            </text>
          </g>
        ))}

        {/* area wash */}
        {areaPath && <path d={areaPath} fill={color} fillOpacity="0.1" />}

        {/* line */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* crosshair */}
        {hover !== null && series[hover] && (
          <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={H - PAD.bottom} stroke={grid} strokeWidth="1" />
        )}

        {/* markers: hovered point + the end point, both with a surface ring */}
        {series.map((d, i) => {
          const isEnd = i === series.length - 1;
          if (!isEnd && hover !== i) return null;
          return (
            <circle key={i} cx={x(i)} cy={y(d.value)} r="4" fill={color} stroke={surface} strokeWidth="2" />
          );
        })}

        {/* direct label on the end point only */}
        {last && (
          <text
            x={x(series.length - 1)}
            y={y(last.value) - 10}
            textAnchor="middle"
            fontSize="11"
            fontWeight="600"
            fill={textPrimary}
          >
            {last.value}
          </text>
        )}

        {/* x labels */}
        {series.map((d, i) => (
          <text key={i} x={x(i)} y={H - PAD.bottom + 16} textAnchor="middle" fontSize="10" fill={textSecondary}>
            {monthLabel(d.label)}
          </text>
        ))}
      </svg>

      {/* tooltip */}
      {hover !== null && series[hover] && (
        <div
          style={{
            position: "absolute",
            left: `${(x(hover) / W) * 100}%`,
            top: 0,
            transform: "translate(-50%, -4px)",
            background: surface,
            border: `1px solid ${grid}`,
            borderRadius: 8,
            padding: "4px 10px",
            fontSize: 12,
            color: textPrimary,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(0,0,0,.12)",
          }}
        >
          <TrendingUp size={11} style={{ verticalAlign: "-1px", marginInlineEnd: 5, color }} />
          {monthLabel(series[hover].label)}: <strong>{series[hover].value}</strong>
        </div>
      )}
    </div>
  );
};

export default HiringTrendChart;
